import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Modal } from '@kep-ui-kit/ui-components';
import { StyledButton, StyledInput, StyledTitle } from '@selfserve/react-ui';
import { SpeakerData } from '../feature/webinar-creator-wizard';
import { FormikProps, useFormik } from 'formik';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import { ImageUploader } from './image-uploader';
import { KavaEventPrefix } from '@selfserve/react-utils';
import MarketingInfoContext from '../../../../react-feature-marketing-info/src/lib/utils/markerting-info-context';

const StyledPlanModal = styled(Modal)(({ theme }: { theme?: any }) => ({
  '.MuiPaper-root': {
    maxWidth: '680px',
    position: 'fixed',
    right: '0px',
    paddingTop: '16px',
    paddingBottom: '16px',
    flexWrap: 'wrap',
    overflowX: 'hidden',
  },
  '.break': {
    background: 'rgba(204, 204, 204, 1)',
    height: '1px',
    display: 'list-item',
  },
  '.explanation': {
    lineHeight: '18px',
    fontSize: theme.typography.pxToRem(14),
  },
  '.controlButtonGroup': {
    display: 'flex',
    flexDirection: 'row-reverse',
    gap: '10px',
    position: 'fixed',
    bottom: '0',
    right: '0',
    maxWidth: '680px',
    width: '100%',
    backgroundColor: 'white',
    borderTop: '1px rgba(204, 204, 204, 1) solid',
    paddingTop: '16px',
    '& button': {
      marginBottom: '16px',
    },
  },
}));

const StyledForm = styled('form')(({ theme }: { theme?: any }) => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& > *': {
    paddingLeft: '40px !important',
    paddingRight: '40px !important',
    marginTop: '12px !important',
    marginBottom: '12px !important',
  },
  '& .kepuikit-base-input-root:not(.multiline-input)': {
    height: '32px',
    maxlength: '2000',
  },
  '& [class*="StyledInputCounter"]': {
    display: 'none',
  },
  '& .input-group': {
    display: 'flex',
    gap: '32px',
    '& .kepuikit-base-input-root': {
      display: 'flex',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '24px',
    },
  },
  '& textarea.kepuikit-base-input-inputMultiline': {
    minHeight: '48px',
    maxHeight: '48px',
    height: '48px',
    maxWidth: '584px',
    minWidth: '584px',
    width: '584px',
    paddingRight: '8px',
    paddingLeft: '8px',
    resize: 'none',
  },
}));

const StyledAddSpeakerTitle = styled(StyledTitle)(
  ({ theme }: { theme?: any }) => ({
    fontSize: theme.typography.pxToRem(18),
    marginBottom: '11px',
    marginTop: '11px',
    lineHeight: '20px',
  })
);

const StyledControlButton = styled(StyledButton)(() => ({
  fontWeight: '600',
}));

interface Props {
  open: boolean;
  handleClose: () => void;
  handleAdd: (speaker: SpeakerData) => void;
  speakerEmails: string[];
}

const initialValues = {
  email: '',
  roles: [],
  firstName: '',
  lastName: '',
  title: '',
  company: '',
  bio: '',
  photo: '',
};

export const SpeakerModal = ({
  open,
  handleClose,
  handleAdd,
  speakerEmails,
}: Props) => {
  const validationSchema = (t: TFunction) =>
    Yup.object().shape({
      email: Yup.string()
        .required(t('webinar-wizard.validations.email-required'))
        .email()
        .test(
          'uniqueness',
          'email must be unique',
          (email) => !speakerEmails.includes(email as string)
        )
        .min(2, ''),
      firstName: Yup.string()
        .required(t('webinar-wizard.validations.first-name-required'))
        .min(2, ''),
      lastName: Yup.string()
        .required(t('webinar-wizard.validations.last-name-required'))
        .min(2, ''),
    });

  const { t } = useTranslation();
  const formik: FormikProps<SpeakerData> = useFormik<SpeakerData>({
    initialValues: initialValues,
    validationSchema: validationSchema(t),
    onSubmit: handleAdd,
  });
  const {
    handleSubmit,
    handleChange,
    resetForm,
    isValid,
    validateForm,
    values: { firstName, lastName, bio, company, email, title },
  } = formik;
  const [isLoading, setLoading] = useState(false);
  const { raiseAnalyticEventsRequest } = useContext(MarketingInfoContext);

  useEffect(() => {
    if (!open) {
      resetForm();
    } else {
      validateForm();
    }
  }, [validateForm, resetForm, open]);

  const submitAndReset = (e: FormEvent<HTMLFormElement>) => {
    raiseAnalyticEventsRequest(`${KavaEventPrefix}SpeakerInvite`);
    handleSubmit(e);
  };

  const closeAndReset = () => {
    raiseAnalyticEventsRequest(`${KavaEventPrefix}SpeakerCancelInvite`);
    handleClose();
  };

  const setImageLoading = useCallback(
    (isLoading: boolean) => {
      setLoading(isLoading);
    },
    [setLoading]
  );

  return (
    <StyledPlanModal
      backdropColor="rgba(34, 34, 34, 0.6)"
      onClose={closeAndReset}
      fullWidth
      open={open}
      fullScreen
    >
      <StyledForm onSubmit={submitAndReset}>
        <StyledAddSpeakerTitle>
          {t('webinar-wizard.add-speaker-invite')}
        </StyledAddSpeakerTitle>
        <div className="break" />
        <span className="explanation">
          {t('webinar-wizard.add-speaker-explanation')}
        </span>

        <StyledInput
          id="email"
          name="email"
          inputLabel={t('webinar-wizard.add-speaker-email')}
          value={email}
          onChange={handleChange}
          inputProps={{ 'aria-label': t('aria.add-speaker-email') }}
          autoFocus
          fullWidth
        />
        <div className="input-group">
          <StyledInput
            id="firstname"
            name="firstName"
            inputLabel={t('webinar-wizard.add-speaker-first-name')}
            value={firstName}
            onChange={handleChange}
            fullWidth
            inputProps={{ 'aria-label': t('aria.add-speaker-first-name') }}
          />

          <StyledInput
            id="lastname"
            name="lastName"
            inputLabel={t('webinar-wizard.add-speaker-last-name')}
            value={lastName}
            onChange={handleChange}
            fullWidth
            inputProps={{ 'aria-label': t('aria.add-speaker-last-name') }}
          />
        </div>

        <ImageUploader
          label={'Speaker Details'}
          editLabel={'Upload'}
          tooltipText={'Recommended size: 500x500 px'}
          type="overlay"
          withoutBackground
          formik={formik}
          valueName="photo"
          callback={setImageLoading}
          eventName={`${KavaEventPrefix}SpeakerUploadImg`}
        />

        <div className="input-group">
          <StyledInput
            id="title"
            name="title"
            inputLabel={t('webinar-wizard.add-speaker-job-title')}
            value={title}
            onChange={handleChange}
            fullWidth
            inputProps={{ 'aria-label': t('aria.add-speaker-job-title') }}
          />

          <StyledInput
            id="company"
            name="company"
            inputLabel={t('webinar-wizard.add-speaker-company')}
            value={company}
            onChange={handleChange}
            fullWidth
            inputProps={{ 'aria-label': t('aria.add-speaker-company') }}
          />
        </div>

        <StyledInput
          maxInputCharacters={2500}
          id="bio"
          name="bio"
          multiline
          minRows={3}
          className="multiline-input"
          inputLabel={t('webinar-wizard.add-speaker-bio')}
          value={bio}
          onChange={handleChange}
          inputProps={{ 'aria-label': t('aria.add-speaker-bio') }}
        />

        <div className="controlButtonGroup">
          <StyledControlButton type="submit" disabled={!isValid || isLoading}>
            {t('webinar-wizard.add-speaker-invite')}
          </StyledControlButton>
          <StyledControlButton
            onMouseDown={(event) => {
              //we need this to prevent double click in case if we have focused input
              event.preventDefault();
              closeAndReset();
            }}
            variant="borderless"
          >
            {t('common.cancel')}
          </StyledControlButton>
        </div>
      </StyledForm>
    </StyledPlanModal>
  );
};
